import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

// Whether a connected chat channel's credentials still work.
//
// A card's "Connected" only means credentials are stored. When the provider
// rejects them (a BotFather /revoke, a reissued LINE token, a Meta token
// killed by a password change), the backend records it in
// channel_connection_health: on the first failed reply, or in the daily
// connection check. See supabase/functions/_shared/connectors/health.ts.
// Reconnecting deletes the record.

export type HealthChannel =
  | "whatsapp"
  | "instagram"
  | "telegram"
  | "facebook_messenger"
  | "line";

export const channelHealthKey = (businessId: string | null | undefined, channel: HealthChannel) =>
  ["channelHealth", businessId, channel] as const;

export function useChannelHealth(channel: HealthChannel) {
  const { user, businessId } = useAuth();
  const { data } = useQuery({
    queryKey: channelHealthKey(businessId, channel),
    enabled: !!user && !!businessId,
    staleTime: 15_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("channel_connection_health")
        .select("status, reason, failed_at")
        .eq("business_id", businessId!)
        .eq("channel", channel)
        .maybeSingle();
      // No record (or the table not there yet) means nothing has failed.
      if (error) return null;
      return data;
    },
  });
  return { authFailed: data?.status === "auth_failed" };
}
